if (!customElements.get('product-gallery-progress')) {
  customElements.define(
    'product-gallery-progress',
    class ProductGalleryProgress extends HTMLElement {
      connectedCallback() {
        this.carousel = document.getElementById(this.getAttribute('aria-controls'));

        if (!this.carousel) return;

        this.progressBar = this.querySelector('.gallery__progress-bar');
        this.frameRequested = false;
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);

        this.carousel.addEventListener('scroll', this.handleScroll, { passive: true });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResize);
          this.resizeObserver.observe(this.carousel);
        } else {
          window.addEventListener('resize', this.handleResize);
        }

        this.mutationObserver = new MutationObserver(this.handleResize);
        this.mutationObserver.observe(this.carousel, {
          subtree: true,
          attributes: true,
          attributeFilter: ['hidden']
        });

        this.update();
      }

      disconnectedCallback() {
        this.carousel?.removeEventListener('scroll', this.handleScroll);
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        window.removeEventListener('resize', this.handleResize);

        if (this.frameRequested) {
          cancelAnimationFrame(this.frameRequested);
        }
      }

      handleScroll() {
        if (this.frameRequested) return;

        this.frameRequested = requestAnimationFrame(() => {
          this.frameRequested = false;
          this.update();
        });
      }

      handleResize() {
        this.update();
      }

      update() {
        if (!this.carousel || !this.progressBar) return;

        const visibleSlides = [
          ...this.carousel.querySelectorAll('.gallery__media:not([data-clone])')
        ].filter((slide) => !slide.hidden);
        const count = visibleSlides.length;

        this.hidden = count <= 1;

        if (count <= 1) return;

        const isRtl = getComputedStyle(this.carousel).direction === 'rtl';

        // Position in media: 0 on the first real media, count - 1 on the last, measured from the first real media so
        // that the loop's leading clone does not count. Towards a loop clone the bar holds its end and moves to the
        // other end halfway, as the clone (a copy of the media there) takes over the screen.
        const step = Math.abs(visibleSlides[1].offsetLeft - visibleSlides[0].offsetLeft) || this.carousel.clientWidth;
        const last = count - 1;
        let position = isRtl
          ? Math.abs(this.carousel.scrollLeft) / step
          : (this.carousel.scrollLeft - visibleSlides[0].offsetLeft) / step;

        if (position > last) position = position - last < 0.5 ? last : 0;
        else if (position < 0) position = position > -0.5 ? 0 : last;

        this.progressBar.style.width = `${100 / count}%`;
        this.progressBar.style.transform = `translateX(${position * 100 * (isRtl ? -1 : 1)}%)`;
        this.setAttribute('aria-valuemin', '1');
        this.setAttribute('aria-valuemax', String(count));
        this.setAttribute('aria-valuenow', String(Math.round(position) + 1));
      }
    }
  );
}

/**
 * Makes the product gallery carousel loop endlessly in both directions.
 *
 * The gallery's <x-carousel> is a native scroll-snap carousel that stops hard at both ends. This element clones the
 * first and last media and, once the scroll momentum has settled on one of those clones, silently repositions the
 * scroller onto the matching real media. Clone and original are identical, and the reposition only happens after the
 * scroll has come to rest, so the seam is invisible.
 *
 * The clones carry [data-clone] and the carousel is given a cell-selector that excludes them, which keeps them
 * invisible to everything in the theme that counts cells: the dots, the thumbnails, the variant image set filtering
 * and the zoom lightbox.
 *
 * `cells` names the carousel's cells (default the gallery's media); product cards loop their images with cells="img".
 */
if (!customElements.get('gallery-loop')) {
  customElements.define(
    'gallery-loop',
    class GalleryLoop extends HTMLElement {
      connectedCallback() {
        this.carousel = document.getElementById(this.getAttribute('carousel'));

        if (!this.carousel) return;

        this.desktopQuery = window.matchMedia('screen and (min-width: 1000px)'); // theme desktop breakpoint, as isDesktop()
        this.handleSettle = this.handleSettle.bind(this);
        this.handleFilter = this.handleFilter.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);

        this.carousel.addEventListener('carousel:settle', this.handleSettle);
        this.carousel.addEventListener('carousel:filter', this.handleFilter);
        this.desktopQuery.addEventListener('change', this.handleBreakpoint);

        this.build();

        // The gallery can still be unmeasurable at this point (collapsed tab, quick buy drawer that has not opened
        // yet). Wait for it to get a width before cloning, otherwise every offset reads as zero.
        if (!this.carousel.clientWidth) {
          this.resizeObserver = new ResizeObserver(() => {
            if (!this.carousel.clientWidth) return;

            this.resizeObserver.disconnect();
            this.resizeObserver = null;
            this.build();
          });

          this.resizeObserver.observe(this.carousel);
        }
      }

      disconnectedCallback() {
        this.carousel?.removeEventListener('carousel:settle', this.handleSettle);
        this.carousel?.removeEventListener('carousel:filter', this.handleFilter);
        this.desktopQuery?.removeEventListener('change', this.handleBreakpoint);
        this.resizeObserver?.disconnect();
      }

      /* The grid media layouts do not scroll horizontally, so looping only applies to them on mobile. */
      get enabledForViewport() {
        return this.desktopQuery.matches ? this.hasAttribute('desktop') : true;
      }

      get cellSelector() {
        return `:scope > ${this.getAttribute('cells') || '.gallery__media'}`;
      }

      get realCells() {
        return [...this.carousel.querySelectorAll(`${this.cellSelector}:not([data-clone])`)].filter(
          (cell) => !cell.hasAttribute('hidden')
        );
      }

      get clones() {
        return [...this.carousel.querySelectorAll('[data-clone]')];
      }

      build() {
        if (!this.carousel || !this.carousel.clientWidth) return;

        this.removeClones();

        const cells = this.realCells;

        if (!this.enabledForViewport || cells.length < 2) return;

        const first = cells[0];
        const last = cells[cells.length - 1];
        const anchor = this.cellNearestCentre() || first;

        // Appending at the end shifts nothing, so only the leading clone needs the scroll position preserving.
        this.carousel.append(this.createClone(first, 'tail'));
        this.preserveScroll(anchor, () => this.carousel.insertBefore(this.createClone(last, 'head'), first));
      }

      removeClones() {
        const clones = this.clones;

        if (!clones.length) return;

        let anchor = this.cellNearestCentre();

        // If the shopper is looking at a clone as it is removed, hold on to the real media that clone copies.
        if (anchor?.hasAttribute('data-clone')) {
          const cells = this.realCells;
          anchor = anchor.getAttribute('data-clone') === 'head' ? cells[cells.length - 1] : cells[0];
        }

        this.preserveScroll(anchor, () => clones.forEach((clone) => clone.remove()));
      }

      /**
       * Adds or removes media in front of the current position while keeping the same media under the shopper's eye.
       *
       * The position is restored to an absolute value rather than nudged by a delta: browsers apply scroll anchoring
       * of their own when content is inserted ahead of the scroll offset, and a relative adjustment ends up counting
       * that shift a second time.
       */
      preserveScroll(anchor, mutate) {
        if (!anchor) {
          mutate();
          return;
        }

        const scrollBefore = this.carousel.scrollLeft;
        const offsetBefore = this.offsetOf(anchor);

        mutate();

        this.carousel.scrollLeft = this.offsetOf(anchor) + (scrollBefore - offsetBefore);
      }

      /**
       * A cell's exact position in the carousel. offsetLeft rounds to whole pixels: on a card 182.5px wide the strip
       * then rested half a pixel into the next image, which showed as a thin line of it along the edge.
       */
      offsetOf(cell) {
        return cell.getBoundingClientRect().left - this.carousel.getBoundingClientRect().left + this.carousel.scrollLeft;
      }

      createClone(source, position) {
        const clone = source.cloneNode(true);

        clone.setAttribute('data-clone', position);
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.remove('is-initial');

        // The media id has to go: the theme looks media up by that attribute when a variant changes, and a clone
        // sitting in front of the real one would win the lookup.
        ['id', 'role', 'aria-label', 'data-media-id', 'data-media-position'].forEach((attribute) =>
          clone.removeAttribute(attribute)
        );
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));

        // Videos and 3D models are reduced to their poster so that a clone never instantiates a second player.
        const type = source.getAttribute('data-media-type');
        if (type && type !== 'image') {
          const poster = clone.querySelector('img');

          if (poster) {
            clone.replaceChildren(poster);
          } else {
            const placeholder = document.createElement('div');
            const ratio = getComputedStyle(source.firstElementChild || source).aspectRatio;

            placeholder.style.aspectRatio = ratio && ratio !== 'auto' ? ratio : '1';
            placeholder.style.width = '100%';
            clone.replaceChildren(placeholder);
          }
        }

        // A clone can be scrolled into view before the media it copies has ever been on screen, so it must not be lazy.
        [clone, ...clone.querySelectorAll('img')].filter((node) => node.tagName === 'IMG').forEach((image) => {
          image.setAttribute('loading', 'eager');
          image.removeAttribute('fetchpriority');
        });

        return clone;
      }

      handleSettle() {
        if (!this.clones.length) return;

        const settled = this.cellNearestCentre();

        if (!settled || !settled.hasAttribute('data-clone')) return;

        const cells = this.realCells;
        const target = settled.getAttribute('data-clone') === 'head' ? cells[cells.length - 1] : cells[0];

        if (!target) return;

        // Shifting by the difference between the two offsets keeps any sub-pixel position inside the cell, so this
        // works the same whether the carousel snaps to the centre, the start or the end of a cell.
        this.carousel.scrollLeft += this.offsetOf(target) - this.offsetOf(settled);
      }

      /* Measured from bounding rectangles rather than scrollLeft so that the reading holds in both writing directions. */
      cellNearestCentre() {
        const bounds = this.carousel.getBoundingClientRect();
        const centre = bounds.left + bounds.width / 2;

        let nearest = null;
        let shortestDistance = Infinity;

        this.carousel.querySelectorAll(this.cellSelector).forEach((cell) => {
          if (cell.hasAttribute('hidden')) return;

          const rect = cell.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - centre);

          if (distance < shortestDistance) {
            shortestDistance = distance;
            nearest = cell;
          }
        });

        return nearest;
      }

      /* A variant image set changes which media is first and last, so the clones are rebuilt rather than reused. */
      handleFilter() {
        requestAnimationFrame(() => this.build());
      }

      handleBreakpoint() {
        this.build();
      }
    }
  );
}

/**
 * Mobile behaviour for the gallery zoom.
 *
 * The gallery only opens the lightbox when an image is clicked on desktop, so on mobile the floating button is the only
 * way in apart from pinching on iOS. This element adds tapping the image itself, and can keep the button out of the
 * way until the shopper actually touches the gallery.
 *
 * It uses the gallery's "lightbox:open" event rather than reaching into <product-gallery>, so the zoom keeps
 * behaving exactly as the theme intends once it is open.
 */
if (!customElements.get('gallery-mobile-zoom')) {
  customElements.define(
    'gallery-mobile-zoom',
    class GalleryMobileZoom extends HTMLElement {
      connectedCallback() {
        this.gallery = this.closest('product-gallery');
        this.carousel = this.gallery?.querySelector('.gallery__track');

        if (!this.carousel) return;

        this.desktopQuery = window.matchMedia('screen and (min-width: 1000px)'); // theme desktop breakpoint, as isDesktop()
        this.button = this.gallery.querySelector('.gallery__zoom');
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);

        this.carousel.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
        this.carousel.addEventListener('pointerup', this.handlePointerUp, { passive: true });
      }

      disconnectedCallback() {
        this.carousel?.removeEventListener('pointerdown', this.handlePointerDown);
        this.carousel?.removeEventListener('pointerup', this.handlePointerUp);
        clearTimeout(this.revealTimeout);
      }

      handlePointerDown(event) {
        this.origin = { x: event.clientX, y: event.clientY, at: Date.now() };
      }

      handlePointerUp(event) {
        if (this.desktopQuery.matches || !this.origin) return;

        // A swipe through the gallery must not be mistaken for a tap, so both the travel and the duration are checked.
        const travelled = Math.hypot(event.clientX - this.origin.x, event.clientY - this.origin.y);
        const elapsed = Date.now() - this.origin.at;

        this.origin = null;

        if (travelled > 10 || elapsed > 500) return;

        if (this.hasAttribute('reveal-button')) {
          this.revealButton();
        }

        if (this.hasAttribute('tap-to-zoom')) {
          this.openLightbox(event.target);
        }
      }

      revealButton() {
        if (!this.button) return;

        clearTimeout(this.revealTimeout);
        this.button.classList.add('is-revealed');
        this.revealTimeout = setTimeout(() => this.button.classList.remove('is-revealed'), 2500); // shown 2.5s after a tap
      }

      openLightbox(target) {
        const media = target?.closest?.('.gallery__media');

        if (!media || media.hasAttribute('data-clone')) return;

        // The lightbox is built from images only, so its indexes have to be counted the same way the theme counts them.
        const imageCells = this.carousel.cells.filter((cell) => cell.getAttribute('data-media-type') === 'image');
        const index = imageCells.indexOf(media);

        if (index === -1) return;

        this.gallery.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index } }));
      }
    }
  );
}
